import { Button } from "@mui/material";
import { AppImageDimension } from "app/appImages";
import axios, { AxiosPromise } from "axios";
import { IconButtonDelete } from "components/iconButtons";
import { useMprcTheme } from "components/MprcThemeProvider";
import { AsyncUploadResult } from "generated/CoreApi_v1";
import * as React from "react";
import { useState } from "react";
import { SessionStore } from "services/SessionStore";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IUploadProps {
    acceptedFiles?: string;
    buttonText?: string;
    currentImageUrl?: string;
    deleteUrl?: string;
    emptyImageUrl?: string;
    enableClear?: boolean;
    forceCreateThumbnail?: boolean;
    height?: number;
    postUrl?: string;
    width?: number;
    boxHeight?: number;
    variant?: "normal" | "boxed";

    onClear?: () => void;
    onFileUploading?: (data: boolean) => void;
    onFileUploadSuccess?: (data: AsyncUploadResult) => void;
    onFileUploadError?: (data: any) => void;
}

interface IUploadState {
    uploadId: string;
    currentFile: any;
    currentPreviewUrl: any;
    lastUploadId: AsyncUploadResult | null;
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const RenderBox: React.FC<{ children?: React.ReactNode; boxHeight?: number; }> = (props) => {
    return (
        <div
            style={
                {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                    backgroundColor: "#e5e5e5",
                    border: "solid 1px #808080",
                    padding: "8px",
                    height: props.boxHeight || "auto",
                }
            }
        >
            {props.children}
        </div>
    );
};

const RenderImage: React.FC<{ url: string, width: number, height: number; }> = (props) => {
    return (
        <img
            alt=""
            height={props.height}
            width={props.width}
            src={props.url}
        />
    );
};

/////////////////////////////////////////////////////////////////////////////////////////////////

export const MprcImageUpload: React.FC<IUploadProps> = (props) => {
    const { theme } = useMprcTheme();
    const [mouseOver, setMouseOver] = useState<boolean>(false);
    const [state, setState] = useState<IUploadState>({
        uploadId: new Date().getTime().toString(),
        currentFile: null,
        currentPreviewUrl: null,
        lastUploadId: null,
    });

    const {
        acceptedFiles = "image/*",
        postUrl = "/api/v1.0/AsyncUpload/Save",
        deleteUrl = "/api/v1.0/AsyncUpload/Remove",
        variant = "normal",
        boxHeight,
        buttonText,
        currentImageUrl,
        emptyImageUrl,
        enableClear,
        forceCreateThumbnail,
        height,
        width,
        onClear,
        onFileUploadError,
        onFileUploading,
        onFileUploadSuccess
    } = props;

    //
    const callServer = (url: string, formData: FormData): AxiosPromise<AsyncUploadResult> => {

        const headers: { [key: string]: string; } = {};
        headers["Accept"] = "application/json";
        headers["Content-Type"] = "multipart/form-data";
        if (SessionStore.bearerString() != "") headers["Authorization"] = SessionStore.bearerString();

        return axios.post<AsyncUploadResult>(url, formData, {
            headers: headers,
        });
    };

    //
    const deleteHandler = (data: AsyncUploadResult): void => {
        if (deleteUrl == null || data == null) return;

        var files = data.mainFile ? [data.mainFile.fileId] : [];
        if (data.thumbnail != null) files.push(data.thumbnail.fileId);

        const formData = new FormData();
        formData.append('filesToRemove', files.join(","));
        callServer(deleteUrl, formData);
    };

    //
    const uploadHandler = (): void => {
        if (postUrl == null) return;

        const formData = new FormData();
        formData.append('file', state.currentFile, state.currentFile.name);
        if (forceCreateThumbnail) formData.append("createThumbnail", "1");

        if (onFileUploading)
            onFileUploading(true);

        callServer(postUrl, formData)
            .then((response) => {
                const oldUploadCompleted = state.lastUploadId;

                setState({
                    ...state,
                    lastUploadId: response.data,
                    currentPreviewUrl: response.data.thumbnail
                        ? response.data.thumbnail.url
                        : response.data.mainFile
                            ? response.data.mainFile.url
                            : emptyImageUrl
                });

                if (onFileUploadSuccess)
                    onFileUploadSuccess(response.data);

                if (onFileUploading)
                    onFileUploading(false);

                if (oldUploadCompleted)
                    deleteHandler(oldUploadCompleted);
            })
            .catch((error: any) => {
                if (onFileUploadError)
                    onFileUploadError(error);

                if (onFileUploading)
                    onFileUploading(false);
            });
    };

    //
    const handleChange = (e: any) => {

        const reader = new FileReader();
        const file = e.target.files[0];
        if (file) {
            reader.onloadend = () => {
                if (reader.result) {
                    setState({
                        ...state,
                        currentFile: file,
                        currentPreviewUrl: reader.result
                    });

                    uploadHandler();
                }
            };

            reader.readAsDataURL(file);
        }
    };

    //
    const handleClear = () => {
        setState({
            ...state,
            currentFile: null,
            currentPreviewUrl: null,
            lastUploadId: null
        });

        onClear && onClear();
    };

    //
    const getImageUrl = (state: IUploadState, props: IUploadProps): string => {
        return state.currentPreviewUrl
            ? state.currentPreviewUrl
            : currentImageUrl
                ? currentImageUrl
                : emptyImageUrl ?? "";
    };

    //
    const getDimensions = () => {
        if (width && height)
            return { width: width, height: height };

        return AppImageDimension.ThumbnailWide;
    };

    //
    const renderClear = () => {
        if (!enableClear || currentImageUrl == null || mouseOver == false)
            return null;

        return (
            <div style={{ position: "absolute", right: 0 }}>
                <IconButtonDelete color="error" size="small" onClick={handleClear} />
            </div>
        );
    };

    //
    //    public shouldComponentUpdate(nextProps: Props, nextState: IUploadState) {
    //    return state.currentFile != nextState.currentFile
    //        || getImageUrl(nextState, nextProps) != getImageUrl(state, props)
    //        || state.mouseOver != nextState.mouseOver
    //        ;
    //}

    //
    const url = getImageUrl(state, props);
    const imgDimensions = getDimensions();

    return (
        <div style={{ textAlign: "center" }}>

            <input
                id={state.uploadId}
                type="file"
                accept={acceptedFiles}
                onChange={(e) => handleChange(e)}
                style={{ display: "none" }}
            />

            <div style={{ position: "relative" }}
                onMouseEnter={() => setMouseOver(true)}
                onMouseLeave={() => setMouseOver(false)}
            >
                {renderClear()}
                {
                    variant == "boxed"
                        ? <RenderBox boxHeight={boxHeight}><RenderImage url={url} width={imgDimensions.width} height={imgDimensions.height} /></RenderBox>
                        : <RenderImage url={url} width={imgDimensions.width} height={imgDimensions.height} />
                }
            </div>

            <br />

            <Button
                onClick={() => document.getElementById(state.uploadId)?.click()}
            >
                {buttonText || theme.MprcImageUpload.selectText}
            </Button>
        </div >
    );
};
